import React, {useCallback, useContext, useMemo} from 'react'
import {useSelector} from 'react-redux'
import {HotelCardContext} from 'components/HotelCardContext'
import {SecondaryOfferList} from 'components/OfferList/SecondaryOfferList'
import {MoreDealsButtonVertical} from 'components/OfferList/SecondaryOfferList/MoreDealsButton/MoreDealsButtonVertical'
import {usePropertyHighlightsAnalyticsPayload} from 'components/PropertyHighlights'
import {getSecondaryOffers} from 'components/SplitBookings/helpers'
import {useDatelessSeoSRP} from 'hooks/useDatelessSeoSRP'
import {useHotelCardActions} from 'hooks/useHotelCardActions'
import {useOfferContainer} from 'hooks/useOfferContainer'
import {useShowAccommodationPage} from 'hooks/useShowAccommodationPage'
import useShowOverlay from 'hooks/useShowOverlay/useShowOverlay'
import {OverlayType} from 'modules/overlay/types'
import {
  getAnchorComplete,
  getIsSearchPending,
  getOfferEntity,
  getSplitBookingDetails
} from 'modules/sapiSearch/selectors'
import {SelectedElement} from 'utils/selectedElement'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Divider} from '@vio/atlas/Divider'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'

export const OfferList = () => {
  const isDatelessSeoSRP = useDatelessSeoSRP()
  const {hotelId, isAnchor} = useContext(HotelCardContext)
  // Added as part of dcbc5616-property-highlights-v1
  const {highlights} = usePropertyHighlightsAnalyticsPayload(hotelId)
  const isSearchPending = useSelector(getIsSearchPending)
  const anchorComplete = useSelector(getAnchorComplete)
  const offerEntity = useSelector(state => getOfferEntity(state, hotelId))
  const {trackUserHotelCardClicked} = useHotelCardActions()
  const showOverlay = useShowOverlay()
  const {getAccommodationPageUrl} = useShowAccommodationPage()
  const {isMobile} = useDeviceLayout()

  const {
    splitBookingOffers,
    showSplitBookingLoader,
    hasSplitBookingOffer,
    splitBookingPosition,
    offerPositionToRemove,
    splitBookingBundle
  } = useSelector(state => getSplitBookingDetails(state, hotelId))

  const {
    search,
    monetisation: {cashback}
  } = useBrandCapabilities()
  const {topOffers, anchorPrice, anchorPriceTotal, allOffers} =
    useOfferContainer({
      offerEntity,
      hotelId
    })

  const searchComplete = !isSearchPending || (anchorComplete && isAnchor)

  const secondaryOffers = useMemo(
    () =>
      getSecondaryOffers({
        topOffers,
        hasSplitBookingOffer,
        splitBookingPosition,
        offerPositionToRemove
      }),
    [
      topOffers,
      hasSplitBookingOffer,
      splitBookingPosition,
      offerPositionToRemove
    ]
  )

  const hasSecondaryOffersToShow = useMemo(() => {
    if (secondaryOffers?.length > 0) {
      return true
    } else if (splitBookingPosition !== 0 && hasSplitBookingOffer) {
      return true
    } else {
      return false
    }
  }, [secondaryOffers, splitBookingPosition, hasSplitBookingOffer])

  const moreDealsHref = !isMobile
    ? getAccommodationPageUrl(hotelId, {showAllOffers: true}) || undefined
    : undefined

  const hasMoreOffers = allOffers.length - topOffers.length > 0

  const hasMoreDealsButton = useMemo(() => {
    return (
      hasSecondaryOffersToShow &&
      search.hasDesktopMoreDealsButton &&
      hasMoreOffers
    )
  }, [
    hasSecondaryOffersToShow,
    search.hasDesktopMoreDealsButton,
    hasMoreOffers
  ])

  const handleSeeMoreClick = useCallback(() => {
    trackUserHotelCardClicked({
      hotelId: hotelId,
      component: 'ListViewHotelCard',
      selectedElement: SelectedElement.ShowAllDeals,
      // Added as part of dcbc5616-property-highlights-v1
      highlights
    })

    if (moreDealsHref) {
      return
    }

    showOverlay({
      type: OverlayType.HotelAllOffers,
      params: {hotelId: hotelId}
    })
  }, [
    trackUserHotelCardClicked,
    hotelId,
    highlights,
    moreDealsHref,
    showOverlay
  ])

  if (isDatelessSeoSRP) {
    return null
  }

  if (!hasSecondaryOffersToShow && searchComplete) {
    return null
  }

  if (!hasSecondaryOffersToShow) {
    return (
      <>
        <Divider sideMargin="s500" />
        <SecondaryOfferList isLoading variant="verticalInline" />
      </>
    )
  }

  return (
    <>
      <Divider sideMargin="s500" />
      <SecondaryOfferList
        variant="verticalInline"
        anchorPrice={anchorPrice}
        anchorPriceTotal={anchorPriceTotal}
        hotelId={hotelId}
        secondaryOffers={secondaryOffers}
        sourceComponent="DesktopTopOffers"
        splitBookingOffers={splitBookingOffers}
        showSplitBookingLoader={showSplitBookingLoader}
        splitBookingPosition={splitBookingPosition}
        splitBookingBundle={splitBookingBundle}
        viewMoreSlot={
          hasMoreDealsButton && (
            <MoreDealsButtonVertical
              href={moreDealsHref}
              onClick={handleSeeMoreClick}
            />
          )
        }
        showCashback={cashback.enabled}
        cashbackVariant="short"
      />
    </>
  )
}
